// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  createTypedSelector,
  isManualCitecInstitution,
  isNuvaraInversisInstitution,
} from '@shared/data-access/utils';
import {
  advisorsAdapter,
  institutionsAdapter,
  pendingInstitutionsAdapter,
} from './clients-state';
import { type RootClientsState } from '@shared/data-access/slices/clients/clients-state';
import type {
  AdvisoryInstitution,
  CreateAdvisoryAccountsCitecRequestBody,
} from '@shared/data-access/types/brokerage-types';

const advisorSelectors = advisorsAdapter.getSelectors();
const institutionSelectors = institutionsAdapter.getSelectors();
const pendingInstitutionsSelectors = pendingInstitutionsAdapter.getSelectors();

const createSelector = createTypedSelector<RootClientsState>();

const EMPTY_INSTITUTIONS: AdvisoryInstitution[] = [];

export const selectClientsState = (state: RootClientsState) => state.clients;

export const selectClient = createSelector(
  selectClientsState,
  state => state.client,
);

export const selectClientId = createSelector(
  selectClient,
  client => client?.id ?? null,
);

export const selectInstitutionsState = createSelector(
  selectClientsState,
  state => state.institutions,
);

export const selectInstitutionsByClientId = createSelector(
  [selectInstitutionsState, (_, client_id: number) => client_id],
  (institutions, client_id) =>
    institutionSelectors
      .selectAll(institutions)
      .filter(entity => entity.client_id === client_id)
      .map(({ client_id: _, ...institution }) => institution),
);

export const selectClientInstitutions = createSelector(
  [selectClientId, selectInstitutionsState],
  (client_id, institutions) =>
    client_id === null
      ? EMPTY_INSTITUTIONS
      : selectInstitutionsByClientId.resultFunc(institutions, client_id),
);

export const selectInstitutionsByClientIdAndIds = createSelector(
  [
    selectInstitutionsState,
    (_, client_id: number, institutionIds: string[]) => ({
      client_id,
      institutionIds,
    }),
  ],
  (institutions, { client_id, institutionIds }) =>
    selectInstitutionsByClientId
      .resultFunc(institutions, client_id)
      .filter(institution => institutionIds.includes(institution.id)),
);

export const selectClientInstitution = createSelector(
  [selectClientInstitutions, (_, institutionId?: string) => institutionId],
  (institutions, institutionId) =>
    institutions.find(institution => institution.id === institutionId),
);

export const selectClientProductsAccounts = createSelector(
  selectClientInstitutions,
  institutions =>
    institutions.map(institution => ({
      ...institution,
      accounts: institution.products.accounts,
    })),
);

export const selectClientInstitutionByInstitutionId = createSelector(
  [selectClientInstitutions, (_, institutionId: string) => institutionId],
  (institutions, institutionId) =>
    institutions.find(institution => institution.id === institutionId),
);

export const selectClientInstitutionsByInstitutionId = createSelector(
  [selectClientInstitutions, (_, institutionId?: string) => institutionId],
  (institutions, institutionId) =>
    institutionId?.toLowerCase() === 'all'
      ? institutions
      : institutions.filter(institution => institution.id === institutionId),
);

export const selectClientInstitutionsIds = createSelector(
  selectClientInstitutions,
  institutions =>
    institutions
      .filter(
        institution =>
          ['wealth_reader'].includes(institution.provider) &&
          !isManualCitecInstitution(institution) &&
          !isNuvaraInversisInstitution(institution),
      )
      .map(institution => institution.id),
);

export const selectClientProductsAccountsByInstitutionId = createSelector(
  [selectClientInstitutions, (_, institutionId?: string) => institutionId],
  (institutions, institutionId) =>
    (institutionId?.toLowerCase() === 'all'
      ? institutions
      : institutions.filter(institution => institution.id === institutionId)
    ).flatMap(institution =>
      institution.products.accounts.map(account => account.iban),
    ),
);

export const selectClientInstitutionsNames = createSelector(
  selectClientInstitutions,
  institutions =>
    new Map(institutions.map(institution => [institution.id, institution.name])),
);

export const selectAdvisorsState = createSelector(
  selectClientsState,
  state => state.advisors,
);

export const selectAllAdvisors = createSelector(
  selectAdvisorsState,
  advisorSelectors.selectAll,
);

export const selectAdvisorById = createSelector(
  [selectAdvisorsState, (_, advisorId: number) => advisorId],
  (advisors, advisorId) => advisorSelectors.selectById(advisors, advisorId),
);

export const selectPendingInstitutionsState = createSelector(
  selectClientsState,
  state => state.pendingInstitutions,
);

export const selectAllPendingInstitutions = createSelector(
  selectPendingInstitutionsState,
  pendingInstitutionsSelectors.selectAll,
);

export const selectClientNuvaraInversisInstitution = createSelector(
  selectClientInstitutions,
  institutions => institutions.find(isNuvaraInversisInstitution),
);

export const selectClientHasNuvaraInversisAccount = createSelector(
  selectClientInstitutions,
  institutions => institutions.some(isNuvaraInversisInstitution),
);

export const selectBankUploadDraft = createSelector(
  selectClientsState,
  state => state.bankUploadDraft,
);

/** True when draft has institution name or any list rows (not an empty shell). */
export const isMeaningfulBankUploadDraft = (
  draft: CreateAdvisoryAccountsCitecRequestBody | null | undefined,
): boolean => {
  if (!draft) {
    return false;
  }

  if (draft.institution?.name?.trim()) {
    return true;
  }

  if ((draft.accounts?.length ?? 0) > 0) {
    return true;
  }

  if ((draft.positions?.length ?? 0) > 0) {
    return true;
  }

  if ((draft.debts?.length ?? 0) > 0) {
    return true;
  }

  if ((draft.transactions?.length ?? 0) > 0) {
    return true;
  }

  return false;
};

export const selectHasBankUploadDraft = createSelector(
  selectBankUploadDraft,
  draft => isMeaningfulBankUploadDraft(draft),
);
